import { PictureAsPdf } from '@mui/icons-material';
import { Button, FormControl, MenuItem, Paper, Select, SelectChangeEvent, Typography } from '@mui/material';
import ResponsiveTooltip from '../../ResponsiveTooltip';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TaxReportFormat } from '../types';

interface DownloadReportCardProps {
    onDownloadPdf: () => void;
    onDownloadExcel: () => void;
    isPlanPurchased?: boolean;
}

const DownloadReportCard: React.FC<DownloadReportCardProps> = ({ 
    onDownloadPdf, 
    onDownloadExcel,
    isPlanPurchased = false 
}) => {
    const { t } = useTranslation();
    const [selectedDownloadType, setSelectedDownloadType] = useState(TaxReportFormat.PDF);

    const handleDownloadTypeChange = (event: SelectChangeEvent) => {
        setSelectedDownloadType(event.target.value as TaxReportFormat);
    };

    const handleDownload = () => {
        if (!isPlanPurchased) return;
        
        switch (selectedDownloadType) {
            case TaxReportFormat.PDF:
                onDownloadPdf();
                break;
            case TaxReportFormat.EXCEL:
                onDownloadExcel();
                break;
            default:
                break;
        }
    };

    const downloadButton = (
        <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2, borderRadius: 1, fontWeight: 'bold' }}
            onClick={handleDownload}
            startIcon={selectedDownloadType === TaxReportFormat.PDF ? <PictureAsPdf /> : null}
            disabled={!isPlanPurchased}
        >
            {t('taxReports.buttons.downloadReport')}
        </Button>
    );

    return (
        <Paper elevation={0} sx={{
            p: 2,
            bgcolor: (theme) => theme.palette.background.paper,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
            <Typography variant="h6" fontWeight="bold">
                {t('taxReports.download.title')}
            </Typography>
            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                <Select
                    value={selectedDownloadType}
                    onChange={handleDownloadTypeChange}
                    displayEmpty
                    sx={{ borderRadius: 1 }}
                    disabled={!isPlanPurchased}
                >
                    <MenuItem value={TaxReportFormat.PDF}>PDF</MenuItem>
                    <MenuItem value={TaxReportFormat.EXCEL}>Excel</MenuItem>
                </Select>
            </FormControl>
            
            {isPlanPurchased ? (
                downloadButton
            ) : (
                <ResponsiveTooltip title={t('taxReports.planNeeded')} placement="top">
                    <span>{downloadButton}</span>
                </ResponsiveTooltip>
            )}
        </Paper>
    );
};

export default DownloadReportCard;
